import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Avatar } from "../components/Avatar";
import { SectionTitle, Loader, Card } from "../components/ui-bits";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Users, Search, Music, Radio, Heart, Download, Trash2, Mic2, Headphones, Image as ImageIcon, Upload, RotateCcw } from "lucide-react";
import { useSiteImages } from "../context/SiteImagesContext";
import { SITE_IMAGE_SLOTS, resolveImg } from "../lib/siteImages";
import { toast } from "sonner";

export default function Admin() {
  const { map, refresh } = useSiteImages();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [searches, setSearches] = useState([]);
  const [prods, setProds] = useState([]);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/users").then((r) => setUsers(r.data));
    api.get("/admin/searches").then((r) => setSearches(r.data));
    api.get("/admin/productions").then((r) => setProds(r.data));
  };
  useEffect(() => { load(); }, []);

  const delUser = async (id) => { if (!window.confirm("Supprimer cet utilisateur ?")) return; await api.delete(`/admin/users/${id}`); toast.success("Utilisateur supprimé"); load(); };
  const delSearch = async (id) => { if (!window.confirm("Supprimer cette recherche ?")) return; await api.delete(`/admin/searches/${id}`); toast.success("Recherche supprimée"); load(); };
  const delProd = async (id) => { if (!window.confirm("Supprimer cette production ?")) return; await api.delete(`/admin/productions/${id}`); toast.success("Production supprimée"); load(); };

  const uploadImage = async (slot, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("slot", slot); fd.append("file", file);
    try {
      await api.post("/admin/site-images", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh(); toast.success("Image mise à jour");
    } catch { toast.error("Erreur upload"); }
  };
  const resetImage = async (slot) => {
    try { await api.delete(`/admin/site-images/${slot}`); await refresh(); toast.success("Image réinitialisée"); }
    catch { toast.error("Erreur"); }
  };

  if (!stats) return <Loader />;

  const cards = [
    { label: "Artistes", value: stats.artists, icon: Mic2 },
    { label: "Beatmakers", value: stats.beatmakers, icon: Headphones },
    { label: "Recherches", value: stats.searches, icon: Search },
    { label: "Productions", value: stats.productions, icon: Music },
    { label: "Propositions", value: stats.submissions, icon: Radio },
    { label: "Réactions", value: stats.reactions, icon: Heart },
    { label: "Téléchargements", value: stats.downloads, icon: Download },
  ];

  return (
    <div>
      <SectionTitle sub="Modération et statistiques de la plateforme.">Administration</SectionTitle>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <Card key={c.label} className="p-4" data-testid={`stat-${c.label.toLowerCase()}`}>
              <Icon size={18} className="text-violet-400 mb-2" />
              <p className="text-2xl font-display font-bold">{c.value}</p>
              <p className="text-xs text-zinc-400">{c.label}</p>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="users">
        <TabsList className="bg-zinc-900 border border-zinc-800 mb-5">
          <TabsTrigger value="users" data-testid="admin-tab-users"><Users size={15} className="mr-1.5" /> Utilisateurs</TabsTrigger>
          <TabsTrigger value="searches" data-testid="admin-tab-searches"><Search size={15} className="mr-1.5" /> Recherches</TabsTrigger>
          <TabsTrigger value="prods" data-testid="admin-tab-productions"><Music size={15} className="mr-1.5" /> Productions</TabsTrigger>
          <TabsTrigger value="images" data-testid="admin-tab-images"><ImageIcon size={15} className="mr-1.5" /> Visuels</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="space-y-2">
            {users.map((u) => (
              <Card key={u.id} className="p-3 flex items-center gap-3" data-testid={`admin-user-${u.id}`}>
                <Avatar user={u} size={40} />
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">@{u.pseudo} <span className="text-xs text-zinc-500 capitalize">· {u.role}</span></p>
                  <p className="text-xs text-zinc-500 truncate">{u.email}</p>
                </div>
                <button onClick={() => delUser(u.id)} data-testid={`admin-delete-user-${u.id}`} className="p-2 text-zinc-500 hover:text-red-400"><Trash2 size={16} /></button>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="searches">
          <div className="space-y-2">
            {searches.map((s) => (
              <Card key={s.id} className="p-3 flex items-center gap-3" data-testid={`admin-search-${s.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{s.title}</p>
                  <p className="text-xs text-zinc-500">@{s.artist?.pseudo} · {s.status} · {s.code}</p>
                </div>
                <button onClick={() => delSearch(s.id)} data-testid={`admin-delete-search-${s.id}`} className="p-2 text-zinc-500 hover:text-red-400"><Trash2 size={16} /></button>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="prods">
          <div className="space-y-2">
            {prods.map((p) => (
              <Card key={p.id} className="p-3 flex items-center gap-3" data-testid={`admin-prod-${p.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{p.title}</p>
                  <p className="text-xs text-zinc-500">@{p.beatmaker?.pseudo} · {p.style}</p>
                </div>
                <button onClick={() => delProd(p.id)} data-testid={`admin-delete-prod-${p.id}`} className="p-2 text-zinc-500 hover:text-red-400"><Trash2 size={16} /></button>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="images">
          <p className="text-sm text-zinc-400 mb-4">Gère les visuels du site. Remplace ou réinitialise chaque emplacement.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SITE_IMAGE_SLOTS.map((s) => (
              <Card key={s.key} className="p-3" data-testid={`admin-image-${s.key}`}>
                <div className="aspect-video rounded-xl overflow-hidden bg-black mb-3 flex items-center justify-center">
                  <img src={resolveImg(map[s.key])} alt={s.label} className="w-full h-full object-contain" />
                </div>
                <p className="font-medium text-sm mb-2">{s.label}</p>
                <div className="flex gap-2">
                  <label className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg btn-primary text-white cursor-pointer" data-testid={`admin-image-upload-${s.key}`}>
                    <Upload size={14} /> Remplacer
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(s.key, e.target.files[0])} />
                  </label>
                  <button onClick={() => resetImage(s.key)} data-testid={`admin-image-reset-${s.key}`}
                    className="px-3 py-2 rounded-lg border border-zinc-700 hover:border-violet-500 text-zinc-300"><RotateCcw size={14} /></button>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
